import React from "react";
import { Link } from "react-router";
import utils from "@utils";
import classNames from "classnames";
import { Image } from "../../../../components";

// eslint-disable-next-line default-param-last
const reportsJobColumn = (reportType = "invoiceHistory", timeZoneId, user) => {
	const { lang } = global;
	const columnArray = [
		{
			property: "invoiceId",
			header: {
				label: lang.t("Invoice ID"),
			},
			cell: {
				formatters: [
					(value, { rowData }) => (
						<div className="text-center">
							<Link
								to={
									rowData.InvoiceId && rowData.InvoiceStatusId
										? {
												pathname: `/payments/${rowData.InvoiceId}`,
												search: rowData.TransactionId
													? `?transactionId=${rowData.TransactionId}`
													: "",
											}
										: null
								}
								className="font-regular font-size-14 padding-0"
							>
								{rowData.InvoiceId}
							</Link>
						</div>
					),
				],
			},
		},
		{
			property: "dateTime",
			header: {
				label: `${lang.t("Date")} / ${lang.t("Time")}`,
			},
			cell: {
				formatters: [
					(value, { rowData: { JobDate, ClockIn, ClockOut } }) => (
						<p className="margin-bottom-0 margin-left-10">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{utils.formatDate(`${JobDate}Z`, "en-US", timeZoneId)}
							</span>
							<Choose>
								<When condition={ClockIn && ClockOut}>
									<span className="font-regular display-block color-blue-bayoux font-size-12">
										{utils.formatTime(
											`${ClockIn}Z`,
											user.UserPreferences !== "useMilitaryTime",
											timeZoneId,
										)}
										{" - "}
										{utils.formatTime(
											`${ClockOut}Z`,
											user.UserPreferences !== "useMilitaryTime",
											timeZoneId,
										)}
									</span>
								</When>
								<Otherwise>
									<span className="font-regular display-block color-blue-bayoux font-size-12">
										No Clock In
									</span>
								</Otherwise>
							</Choose>
						</p>
					),
				],
			},
		},
		{
			property: "InvoiceDate",
			header: {
				label: "Invoiced Date",
			},
			cell: {
				formatters: [
					value => (
						<p className="margin-bottom-0">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{utils.formatDate(`${value}Z`, "en-US", timeZoneId)}
							</span>
						</p>
					),
				],
			},
		},
		{
			property: "address",
			header: {
				label: lang.t("Location"),
			},
			cell: {
				formatters: [
					(value, { rowData: { Location, LocationDetails } }) => (
						<p className="margin-bottom-0 margin-left-10">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{Location}
							</span>
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{LocationDetails}
							</span>
						</p>
					),
				],
			},
		},
		{
			property: "officer",
			header: {
				label: lang.t("Officer"),
			},
			cell: {
				formatters: [
					(value, { rowData: { OfficerName, OfficerLocation } }) => (
						<p className="margin-bottom-0 margin-left-10">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{OfficerName}
							</span>
							<span className="font-regular display-block color-blue-bayoux font-size-12">
								{OfficerLocation}
							</span>
						</p>
					),
				],
			},
		},
		{
			property: "transactionId",
			header: {
				label: lang.t("Transaction ID"),
			},
			cell: {
				formatters: [
					(value, { rowData }) => (
						<div className="text-center">
							<Link
								to={
									rowData.InvoiceStatusId && rowData.TransactionId
										? `/payments/receipt/${rowData.TransactionId}`
										: null
								}
								className="font-regular font-size-14 padding-0"
							>
								{rowData.TransactionId}
							</Link>
						</div>
					),
				],
			},
		},
		{
			property: "PaymentDate",
			header: {
				label: "Payment Date",
			},
			cell: {
				formatters: [
					value => (
						<p className="margin-bottom-0">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{value ? utils.formatDate(`${value}Z`, "en-US", timeZoneId) : ""}
							</span>
						</p>
					),
				],
			},
		},
		{
			property: "amount",
			header: {
				label: lang.t("Amount"),
			},
			cell: {
				formatters: [
					(value, { rowData: { Amount } }) => (
						<span className="margin-left-10 font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(Amount || 0)}
						</span>
					),
				],
			},
		},
	];

	if (reportType === "invoiceHistory") {
		columnArray.splice(4, 0, {
			property: "status",
			header: {
				label: lang.t("Status"),
			},
			cell: {
				formatters: [
					(
						value,
						{
							rowData: {
								statusObj: { label, value: data },
							},
						},
					) => (
						<span className={classNames("margin-left-10 new-label", `label-${label}`)}>
							{data}
						</span>
					),
				],
			},
		});
	}

	return columnArray;
};

const paymentReportsColumn = type => {
	const { lang } = global;
	const columnArray = [
		{
			property: "details",
			header: {
				label: `${type} ${lang.t("Name")}`,
			},
			cell: {
				formatters: [
					(
						value,
						{ rowData: { Name, CoordinatorName, ProfileImageUrl, DepartmentName } },
					) => (
						<div className="flex mobile-max-width">
							<If condition={type === "Officer"}>
								<Image
									className="height-30 width-30 border-radius-15 margin-right-10 img-cover"
									src={ProfileImageUrl || ""}
									fallbackSrc="avatar"
								/>
							</If>
							<p className="margin-bottom-0">
								<span className="font-regular display-block color-blue-cloud-burst font-size-14">
									{type === "Coordinator" ? CoordinatorName : Name}
								</span>
								<span className="font-regular display-block color-blue-bayoux font-size-12">
									{DepartmentName || ""}
								</span>
							</p>
						</div>
					),
				],
			},
		},
		{
			property: "jobsWorked",
			header: {
				label: type === "Coordinator" ? lang.t("Jobs Coordinated") : lang.t("Jobs Worked"),
			},
			cell: {
				formatters: [
					(value, { rowData: { WorkedJobs, JobsWorked } }) => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{type === "Coordinator" ? JobsWorked : WorkedJobs}
						</span>
					),
				],
			},
		},
		{
			property: "invoicesPaid",
			header: {
				label: lang.t("# Invoices Paid"),
			},
			cell: {
				formatters: [
					(value, { rowData: { PaidInvoices, InvoicesPaid } }) => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{type === "Coordinator" ? InvoicesPaid : PaidInvoices}
						</span>
					),
				],
			},
		},
		{
			property: "total",
			header: {
				label: lang.t("Total Paid"),
			},
			cell: {
				formatters: [
					(value, { rowData: { TotalPaid } }) => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(TotalPaid || 0)}
						</span>
					),
				],
			},
		},
	];

	if (type === "Location") {
		columnArray.splice(1, 0, {
			property: "address",
			header: {
				label: lang.t("Address"),
			},
			cell: {
				formatters: [
					(value, { rowData: { LocationAddress } }) => (
						<p className="margin-bottom-0">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{LocationAddress}
							</span>
						</p>
					),
				],
			},
		});
	}

	if (type === "Client") {
		columnArray.splice(
			1,
			0,
			{
				property: "numberLocations",
				header: {
					label: lang.t("Locations"),
				},
				cell: {
					formatters: [
						(value, { rowData: { NumberLocations } }) => (
							<p className="margin-bottom-0">
								<span className="font-regular display-block color-blue-cloud-burst font-size-14">
									{NumberLocations}
								</span>
							</p>
						),
					],
				},
			},
			{
				property: "numberProjects",
				header: {
					label: lang.t("Projects"),
				},
				cell: {
					formatters: [
						(value, { rowData: { NumberProjects } }) => (
							<p className="margin-bottom-0">
								<span className="font-regular display-block color-blue-cloud-burst font-size-14">
									{NumberProjects}
								</span>
							</p>
						),
					],
				},
			},
		);
	}

	return columnArray;
};

const detailedJobReportsColumn = (
	user,
	workersCompensationFeatureFlag = false,
	agencyAllowedToUseExcludeOfficerRate = false,
) => {
	const { lang } = global;
	const {
		Organization: { PayCodesEnabled },
	} = user;

	const columnArray = [
		{
			property: "JobEventId",
			header: {
				label: lang.t("Job ID"),
			},
			cell: {
				formatters: [
					value => (
						<Link
							to={`/jobs/${value}`}
							className="font-regular"
						>
							{value}
						</Link>
					),
				],
			},
		},
		{
			property: "AssignmentId",
			header: {
				label: lang.t("Assignment"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "InvoiceId",
			header: {
				label: lang.t("Invoice ID"),
			},
			cell: {
				formatters: [
					(value, { rowData: { IsRKPayJob, InvoiceNumber } }) => {
						let route = null;
						if (!IsRKPayJob && InvoiceNumber)
							route = `/payments/manualInvoice?id=${value}`;
						else if (IsRKPayJob && value) route = `/payments/${value}`;

						if (route !== null) {
							return (
								<Link
									id={`go-to-invoice-${InvoiceNumber || value}`}
									to={route}
									className="font-regular"
								>
									{InvoiceNumber || value}
								</Link>
							);
						}
						return <span className="font-regular">Not Invoiced</span>;
					},
				],
			},
		},
		{
			property: "JobStartTime",
			header: {
				label: lang.t("Date"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular display-block color-blue-cloud-burst font-size-14">
							{utils.formatDate(`${value}Z`, "en-US", user.TimeZoneId)}
						</span>
					),
				],
			},
		},
		{
			property: "JobStartTime",
			header: {
				label: lang.t("Start Time"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.formatTime(
								`${value}Z`,
								user.UserPreferences !== "useMilitaryTime",
								user.TimeZoneId,
							)}
						</span>
					),
				],
			},
		},
		{
			property: "Client",
			header: {
				label: lang.t("Client"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "Project",
			header: {
				label: lang.t("Project"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "JobEventName",
			header: {
				label: lang.t("Job Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "TagName",
			header: {
				label: lang.t("Tags"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "JobLocation",
			header: {
				label: lang.t("Location"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "OfficerRank",
			header: {
				label: "Officer Rank",
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "officer",
			header: {
				label: lang.t("Officer"),
			},
			cell: {
				formatters: [
					(value, { rowData }) => (
						<p className="margin-bottom-0 margin-left-10">
							<span className="font-regular display-block color-blue-cloud-burst font-size-14">
								{rowData.OfficerName}
							</span>
							<span className="font-regular display-block color-blue-bayoux font-size-12">
								{rowData.OfficerLocation}
							</span>
						</p>
					),
				],
			},
		},
		{
			property: "OfficerEmail",
			header: {
				label: lang.t("EMail"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "OfficerMobile",
			header: {
				label: lang.t("Mobile"),
			},
			cell: {
				formatters: [
					value => (
						<If condition={value}>
							<span className="font-regular color-blue-cloud-burst font-size-14">
								{utils.formatPhoneNumber(value)}
							</span>
						</If>
					),
				],
			},
		},
		{
			property: "JobRate",
			header: {
				label: lang.t("Rate"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
		{
			property: "JobHourScheduled",
			header: {
				label: lang.t("Scheduled Hours"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "JobClockIn",
			header: {
				label: lang.t("Actual Clock In"),
			},
			cell: {
				formatters: [
					value => (
						<If condition={value}>
							<span className="font-regular color-blue-cloud-burst font-size-14">
								{utils.formatTime(
									`${value}Z`,
									user.UserPreferences !== "useMilitaryTime",
									user.TimeZoneId,
								)}
							</span>
						</If>
					),
				],
			},
		},
		{
			property: "JobClockOut",
			header: {
				label: lang.t("Actual Clock Out"),
			},
			cell: {
				formatters: [
					value => (
						<If condition={value}>
							<span className="font-regular color-blue-cloud-burst font-size-14">
								{utils.formatTime(
									`${value}Z`,
									user.UserPreferences !== "useMilitaryTime",
									user.TimeZoneId,
								)}
							</span>
						</If>
					),
				],
			},
		},
		{
			property: "JobHourWorked",
			header: {
				label: lang.t("Actual Hours"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "TimecardClockin",
			header: {
				label: lang.t("Timecard Clockin"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular display-block color-blue-cloud-burst font-size-14">
							{utils.formatTime(
								`${value}Z`,
								user.UserPreferences !== "useMilitaryTime",
								user.TimeZoneId,
							)}
						</span>
					),
				],
			},
		},
		{
			property: "TimecardClockout",
			header: {
				label: lang.t("Timecard Clockout"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular display-block color-blue-cloud-burst font-size-14">
							{utils.formatTime(
								`${value}Z`,
								user.UserPreferences !== "useMilitaryTime",
								user.TimeZoneId,
							)}
						</span>
					),
				],
			},
		},
		{
			property: "TimecardHours",
			header: {
				label: lang.t("Timecard Hours"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "TimecardNote",
			header: {
				label: lang.t("Timecard Note"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "HasVehicle",
			header: {
				label: lang.t("Vehicle"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value === true ? "Yes" : "No"}
						</span>
					),
				],
			},
		},

		{
			property: "VehicleNumber",
			header: {
				label: lang.t("Vehicle Number"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},

		{
			property: "VehicleType",
			header: {
				label: lang.t("Vehicle Type"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},

		{
			property: "VehicleMileageIn",
			header: {
				label: lang.t("Clock in Vehicle Mileage"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},

		{
			property: "VehicleMileageOut",
			header: {
				label: lang.t("Clock out Vehicle Mileage"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "JobInvoicedTypeId",
			header: {
				label: lang.t("Invoicing Type"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.formatJobInvoicedType(value)}
						</span>
					),
				],
			},
		},
		{
			property: "IsRKInsurance",
			header: {
				label: lang.t("Insurance"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value === true ? "Yes" : "No"}
						</span>
					),
				],
			},
		},
		{
			property: "IsInvoiced",
			header: {
				label: lang.t("Invoiced"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value === true ? "Yes" : "No"}
						</span>
					),
				],
			},
		},
		{
			property: "InvoiceStatus",
			header: {
				label: lang.t("Invoice Status"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "InvoicedHours",
			header: {
				label: lang.t("Invoiced Hours"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		},
		{
			property: "InvoicedAmount",
			header: {
				label: lang.t("Invoice Total"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},

		{
			property: "OfficerPay",
			header: {
				label: lang.t("Officer Pay"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},

		{
			property: "AgencyFees",
			header: {
				label: lang.t("Agency Fees"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},

		{
			property: "VehicleFees",
			header: {
				label: lang.t("Vehicle Fees"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
	];

	if (workersCompensationFeatureFlag) {
		const wcFeeColumn = {
			property: "IsWorkerCompensationInsurance",
			header: {
				label: lang.t("Workers Comp"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value === true ? "Yes" : "No"}
						</span>
					),
				],
			},
		};

		const index = columnArray.findIndex(i => i.property === "IsRKInsurance");

		columnArray.splice(index, 0, wcFeeColumn);
	}

	if (agencyAllowedToUseExcludeOfficerRate) {
		const ExcludeofficerRateColumn = {
			property: "ExcludeOfficerRate",
			header: {
				label: lang.t("ExcludeOfficerPay"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value === true ? "Yes" : "No"}
						</span>
					),
				],
			},
		};

		const index = columnArray.findIndex(i => i.property === "AgencyFees");

		columnArray.splice(index, 0, ExcludeofficerRateColumn);
	}

	if (user.isSuperAdmin() || user.isLEA()) {
		columnArray.push({
			property: "",
			header: {
				label: "Instant Pay",
			},
			cell: {
				formatters: [
					(value, { rowData: { SameDayPayment } }) => (
						<div className="margin-bottom-0">
							<If condition={SameDayPayment}>
								<div className="text-nowrap">
									<span className="font-bold">Created: </span>
									<span>
										{utils.formatDate(
											`${SameDayPayment.Created}Z`,
											"en-US",
											user.TimeZoneId,
											user.UserPreferences === "useMilitaryTime"
												? "dt-num-mil"
												: "dt-num",
										)}
									</span>
								</div>
								<div className="text-nowrap">
									<span className="font-bold">Fee: </span>
									<span>{SameDayPayment.Fee}</span>
								</div>
								<div className="text-nowrap">
									<span className="font-bold">Invoice Rate: </span>
									<span>{SameDayPayment.Rate}</span>
								</div>
								<div className="text-nowrap">
									<span className="font-bold">Invoice Hours: </span>
									<span>{SameDayPayment.Hours}</span>
								</div>
								<div className="text-nowrap">
									<span className="font-bold">Amount: </span>
									<span>{SameDayPayment.Amount}</span>
								</div>
							</If>
						</div>
					),
				],
			},
		});
	}

	if (PayCodesEnabled) {
		columnArray.push({
			property: "PayCodeName",
			header: {
				label: lang.t("Pay Code Short Code"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		});

		columnArray.push({
			property: "PayCodeDescription",
			header: {
				label: lang.t("Pay Code Description"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-cloud-burst font-size-14">
							{value}
						</span>
					),
				],
			},
		});
	}

	return columnArray;
};

const jurisdictionFeePaymentColumns = () => {
	const { lang } = global;
	return [
		{
			property: "FeeTypeName",
			header: {
				label: lang.t("Fee Type"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalJobs",
			header: {
				label: lang.t("Total Jobs"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalInvoices",
			header: {
				label: lang.t("Invoices Submitted"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalInvoicesPaid",
			header: {
				label: lang.t("# Invoices Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalFeesInvoicedAmount",
			header: {
				label: lang.t("Total Invoiced Amount"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
		{
			property: "TotalFeesPaidAmount",
			header: {
				label: lang.t("Total Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
	];
};

const clientPaymentColumns = () => {
	const { lang } = global;
	return [
		{
			property: "Name",
			header: {
				label: lang.t("Client Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalNumberOfInvoices",
			header: {
				label: lang.t("Invoices Submitted"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalNumberOfInvoicesPaid",
			header: {
				label: lang.t("# Invoices Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalInvoiced",
			header: {
				label: lang.t("Total Invoiced"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
		{
			property: "TotalPaid",
			header: {
				label: lang.t("Total Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
	];
};

const locationPaymentColumns = () => {
	const { lang } = global;
	return [
		{
			property: "Name",
			header: {
				label: lang.t("Client Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "LocationName",
			header: {
				label: lang.t("Location Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "LocationDetails",
			header: {
				label: lang.t("Address"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalNumberOfInvoices",
			header: {
				label: lang.t("Invoices Submitted"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalNumberOfInvoicesPaid",
			header: {
				label: lang.t("# Invoices Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalInvoiced",
			header: {
				label: lang.t("Total Invoiced"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
		{
			property: "TotalPaid",
			header: {
				label: lang.t("Total Paid"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
	];
};

const invoicePaymentColumns = (timeZoneId, user) => {
	const { lang } = global;
	const columnArray = [
		{
			property: "InvoiceId",
			header: {
				label: lang.t("Invoice ID"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "InvoiceDateTime",
			header: {
				label: `${lang.t("Date")} / ${lang.t("Time")}`,
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular display-block color-blue-oxford font-size-14">
							{utils.formatDate(
								`${value}Z`,
								"en-US",
								timeZoneId,
								user.UserPreferences === "useMilitaryTime"
									? "dt-num-mil"
									: "dt-num",
							)}
						</span>
					),
				],
			},
		},
		{
			property: "LocationName",
			header: {
				label: lang.t("Location Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "OfficerName",
			header: {
				label: lang.t("Officer Name"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "InvoiceStatus",
			header: {
				label: lang.t("Invoice Status"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TransactionId",
			header: {
				label: lang.t("Transaction ID"),
			},
			cell: {
				formatters: [
					value => (
						<span className="font-regular color-blue-oxford font-size-14">{value}</span>
					),
				],
			},
		},
		{
			property: "TotalFees",
			header: {
				label: lang.t("Amount"),
			},
			cell: {
				formatters: [
					value => (
						<span className="margin-left-10 font-regular color-blue-oxford font-size-14">
							{utils.currencyFormatter(value || 0)}
						</span>
					),
				],
			},
		},
	];

	return columnArray;
};

const fairnessColumns = () => [
	{
		property: "Name",
		header: {
			label: "Officer Name",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "Email",
		header: {
			label: "Email Address",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "Jurisdiction",
		header: {
			label: "Department",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "JobsScheduled",
		header: {
			label: "Jobs Scheduled",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "JobsApplied",
		header: {
			label: "Jobs Applied For",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "JobsScheduled30Day",
		header: {
			label: "30 Day Jobs Scheduled",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "HoursScheduled30Day",
		header: {
			label: "30 Day Hours Scheduled",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
	{
		property: "JobsScheduledPercentage",
		header: {
			label: "% of Total Jobs Scheduled",
		},
		cell: {
			formatters: [value => <span>{value}</span>],
		},
	},
];

export {
	reportsJobColumn,
	paymentReportsColumn,
	detailedJobReportsColumn,
	jurisdictionFeePaymentColumns,
	clientPaymentColumns,
	locationPaymentColumns,
	invoicePaymentColumns,
	fairnessColumns,
};
